import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'iconify-icon';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import FileUploader from '../../components/admin/FileUploader';
import RichTextEditor from '../../components/admin/RichTextEditor';
import EneaflixBulkUpload from '../../components/admin/EneaflixBulkUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Generic Manager Component
const ContentManager = ({ type, title, fields, icon, idField }) => {
  // Each collection's natural id field. Defaults to `${singular}_id` but can
  // be overridden — needed for `eneaflix` which is already singular and
  // would otherwise be sliced to `eneafli_id` (does not exist on the doc).
  const resolvedIdField = idField || `${type.endsWith('s') ? type.slice(0, -1) : type}_id`;
  const getItemId = (item) => item?.[resolvedIdField] || item?.id;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const endpoint = `${API_URL}/admin/${type}`;

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = async () => {
    try {
      const response = await axios.get(endpoint, { withCredentials: true });
      setItems(response.data);
    } catch (error) {
      toast.error(`Erro ao carregar ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        const itemId = getItemId(editingItem);
        if (!itemId) {
          toast.error('ID do item não encontrado');
          return;
        }
        await axios.put(`${endpoint}/${itemId}`, formData, {
          withCredentials: true
        });
        toast.success(`${title} atualizado com sucesso!`);
      } else {
        await axios.post(endpoint, formData, { withCredentials: true });
        toast.success(`${title} criado com sucesso!`);
      }
      setIsDialogOpen(false);
      resetForm();
      loadItems();
    } catch (error) {
      toast.error(error?.response?.data?.detail || `Erro ao salvar ${title.toLowerCase()}`);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const data = {};
    fields.forEach((field) => {
      data[field.name] = item[field.name] || '';
    });
    setFormData(data);
    setIsDialogOpen(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm(`Tem certeza que deseja excluir?`)) return;
    
    try {
      await axios.delete(`${endpoint}/${itemId}`, { withCredentials: true });
      toast.success(`${title} excluído com sucesso!`);
      loadItems();
    } catch (error) {
      toast.error(`Erro ao excluir ${title.toLowerCase()}`);
    }
  };

  const resetForm = () => {
    const data = {};
    fields.forEach((field) => {
      data[field.name] = field.default || '';
    });
    setFormData(data);
    setEditingItem(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold mb-2">{title}</h2>
          <p className="text-neutral-600">Gerencie {title.toLowerCase()}</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
          className="flex items-center gap-2"
        >
          <iconify-icon icon={icon}></iconify-icon>
          Novo
        </Button>
      </div>

      {/* Items List */}
      <div className="bg-white rounded-xl border border-neutral-200 divide-y divide-neutral-200">
        {items.map((item) => {
          const itemId = getItemId(item) || JSON.stringify(item).slice(0, 24);
          return (
          <div key={itemId} className="p-6 hover:bg-neutral-50 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {fields.slice(0, 3).map((field) => {
                  const raw = item[field.name];
                  const isMedia = ['upload-image', 'upload-video', 'upload-document'].includes(field.type);
                  const isRich = field.type === 'richtext';
                  const isImage = isMedia && /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(raw || '');
                  if (isMedia && raw) {
                    return isImage ? (
                      <img key={field.name} src={raw} alt="" className="w-16 h-16 object-cover rounded mt-2" />
                    ) : (
                      <a key={field.name} href={raw} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block mt-1">
                        {raw.split('/').pop()}
                      </a>
                    );
                  }
                  let display = raw || '';
                  if (isRich) display = (display.replace(/<[^>]+>/g, '').slice(0, 100) || '') + '…';
                  else if (field.type === 'textarea') display = (display || '').substring(0, 100) + '…';
                  return (
                    <p
                      key={field.name}
                      className={field === fields[0] ? 'font-semibold text-lg mb-1' : 'text-sm text-neutral-600'}
                    >
                      {display}
                    </p>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleEdit(item)}
                  className="p-2 text-zinc-700 hover:text-accent-orange hover:bg-neutral-200 rounded-lg transition-colors"
                  aria-label="Editar"
                  title="Editar"
                >
                  <iconify-icon icon="solar:pen-linear" className="text-xl"></iconify-icon>
                </button>
                <button
                  onClick={() => handleDelete(itemId)}
                  className="p-2 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-colors"
                  aria-label="Excluir"
                  title="Excluir"
                >
                  <iconify-icon icon="solar:trash-bin-linear" className="text-xl"></iconify-icon>
                </button>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {items.length === 0 && (
        <div className="text-center py-12 text-neutral-500 bg-white rounded-xl border border-neutral-200">
          <iconify-icon icon={icon} className="text-6xl mb-4"></iconify-icon>
          <p>Nenhum item criado ainda</p>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? `Editar ${title}` : `Novo ${title}`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            {fields.map((field) => (
              <div key={field.name}>
                <Label htmlFor={field.name}>
                  {field.label} {field.required && '*'}
                </Label>
                {field.type === 'textarea' ? (
                  <textarea
                    id={field.name}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    className="w-full border border-neutral-300 rounded-md p-2 min-h-[100px]"
                    required={field.required}
                  />
                ) : field.type === 'richtext' ? (
                  <RichTextEditor
                    value={formData[field.name] || ''}
                    onChange={(html) => setFormData({ ...formData, [field.name]: html })}
                    folder={field.folder || type}
                    placeholder={field.placeholder}
                  />
                ) : field.type === 'upload-image' || field.type === 'upload-video' || field.type === 'upload-document' ? (
                  <FileUploader
                    value={formData[field.name] || ''}
                    onChange={(url) => setFormData({ ...formData, [field.name]: url })}
                    accept={field.type.split('-')[1]}
                    folder={field.folder || type}
                    label={field.placeholder}
                    testId={`uploader-${field.name}`}
                  />
                ) : (
                  <Input
                    id={field.name}
                    name={field.name}
                    type={field.type || 'text'}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    required={field.required}
                  />
                )}
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">{editingItem ? 'Atualizar' : 'Criar'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Testimonials Manager
export const TestimonialsManager = () => (
  <ContentManager
    type="testimonials"
    title="Depoimentos"
    icon="solar:chat-square-like-linear"
    fields={[
      { name: 'name', label: 'Nome', required: true },
      { name: 'company', label: 'Empresa', required: true },
      { name: 'role', label: 'Cargo' },
      { name: 'text', label: 'Depoimento', type: 'textarea', required: true },
      { name: 'image', label: 'Foto', type: 'upload-image', folder: 'testimonials' },
      { name: 'video_url', label: 'Vídeo (arquivo ou URL externa)', type: 'upload-video', folder: 'testimonials' },
      { name: 'rating', label: 'Avaliação (1-5)', type: 'number', default: 5 }
    ]}
  />
);

// FAQ Manager
export const FAQManager = () => (
  <ContentManager
    type="faq"
    title="FAQ"
    icon="solar:question-square-linear"
    fields={[
      { name: 'question', label: 'Pergunta', required: true },
      { name: 'answer', label: 'Resposta', type: 'textarea', required: true },
      { name: 'category', label: 'Categoria' },
      { name: 'order', label: 'Ordem', type: 'number', default: 0 }
    ]}
  />
);

// Eneaflix Manager — adds a bulk poster upload panel above the standard CRUD
export const EneaflixManager = () => {
  // bump this whenever the bulk upload completes so the inner ContentManager
  // remounts and re-fetches the freshly-associated images
  const [refreshKey, setRefreshKey] = React.useState(0);
  return (
    <div>
      <EneaflixBulkUpload onComplete={() => setRefreshKey((k) => k + 1)} />
      <ContentManager
        key={refreshKey}
        type="eneaflix"
        title="ENEAFLIX"
        icon="solar:videocamera-linear"
        fields={[
          { name: 'title', label: 'Título', required: true },
          { name: 'type', label: 'Tipo (Filme/Série/Documentário)', required: true },
          { name: 'enneagram_type', label: 'Tipo de Eneagrama (ex: Tipo 1)', required: true },
          { name: 'subtype', label: 'Subtipo (Preservação / Sexual / Social)' },
          { name: 'character', label: 'Personagem analisado' },
          { name: 'description', label: 'Descrição', type: 'textarea', required: true },
          { name: 'image', label: 'Pôster (upload)', type: 'upload-image', folder: 'eneaflix' },
          { name: 'trailer_url', label: 'Vídeo / Trailer (arquivo ou URL)', type: 'upload-video', folder: 'eneaflix' },
        ]}
      />
    </div>
  );
};

export default ContentManager;
